import { useEffect, lazy, Suspense } from 'react';
import type { ComponentType } from 'react';
import { Routes, Route } from 'react-router-dom';

// Eager — vstupná stránka (entry point pre nezalogovaných userov)
import { Home } from './pages/Home';

// Lazy s auto-reloadom: po deployi sa zmenia hashe chunkov a starý tab
// drží neplatné názvy súborov → import zlyhá. Spravíme jeden reload
// (s poistkou proti slučke), aby používateľ dostal nový index.html.
function lazyRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const m = await factory();
      sessionStorage.removeItem('chunk-reload');
      return m;
    } catch (err) {
      if (!sessionStorage.getItem('chunk-reload')) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {}); // stránka sa reloadne, nikdy neresolvne
      }
      throw err;
    }
  });
}

// Lazy — všetky ostatné stránky (každá svoj chunk)
const Login              = lazyRetry(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register           = lazyRetry(() => import('./pages/Register').then(m => ({ default: m.Register })));
const Profile            = lazyRetry(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const ForgotPassword     = lazyRetry(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword      = lazyRetry(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const EditProfile        = lazyRetry(() => import('./pages/EditProfile').then(m => ({ default: m.EditProfile })));
const Questionnaire      = lazyRetry(() => import('./pages/Questionnaire').then(m => ({ default: m.Questionnaire })));
const ChangePassword     = lazyRetry(() => import('./pages/ChangePassword').then(m => ({ default: m.ChangePassword })));
const Catalog            = lazyRetry(() => import('./pages/Catalog').then(m => ({ default: m.Catalog })));
const UserProfile        = lazyRetry(() => import('./pages/UserProfile').then(m => ({ default: m.UserProfile })));
const Discover           = lazyRetry(() => import('./pages/Discover').then(m => ({ default: m.Discover })));
const Likes              = lazyRetry(() => import('./pages/Likes').then(m => ({ default: m.Likes })));
const ChatList           = lazyRetry(() => import('./pages/ChatList').then(m => ({ default: m.ChatList })));
const ChatConversation   = lazyRetry(() => import('./pages/ChatConversation').then(m => ({ default: m.ChatConversation })));
const VerifyEmail        = lazyRetry(() => import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const Onboarding         = lazyRetry(() => import('./pages/Onboarding').then(m => ({ default: m.Onboarding })));
const NotFound           = lazyRetry(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));
const VerifyProfile      = lazyRetry(() => import('./pages/VerifyProfile').then(m => ({ default: m.VerifyProfile })));
const FeaturedBooking    = lazyRetry(() => import('./pages/FeaturedBooking').then(m => ({ default: m.FeaturedBooking })));
const Plus               = lazyRetry(() => import('./pages/Plus').then(m => ({ default: m.Plus })));
const AccountBanned      = lazyRetry(() => import('./pages/AccountBanned').then(m => ({ default: m.AccountBanned })));
const Settings           = lazyRetry(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const ProfileStats       = lazyRetry(() => import('./pages/ProfileStats').then(m => ({ default: m.ProfileStats })));
const BlockedUsers       = lazyRetry(() => import('./pages/BlockedUsers').then(m => ({ default: m.BlockedUsers })));
const PaymentHistory     = lazyRetry(() => import('./pages/PaymentHistory').then(m => ({ default: m.PaymentHistory })));
const Terms              = lazyRetry(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy            = lazyRetry(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Cookies            = lazyRetry(() => import('./pages/Cookies').then(m => ({ default: m.Cookies })));
const WaitingArea        = lazyRetry(() => import('./pages/WaitingArea').then(m => ({ default: m.WaitingArea })));
const Faq                = lazyRetry(() => import('./pages/Faq').then(m => ({ default: m.Faq })));
const Support            = lazyRetry(() => import('./pages/Support').then(m => ({ default: m.Support })));

// Admin — lazy, oddelený chunk pre adminov
const AdminLogin         = lazyRetry(() => import('./pages/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminLayout        = lazyRetry(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminDashboard     = lazyRetry(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminUsers         = lazyRetry(() => import('./pages/admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminReports       = lazyRetry(() => import('./pages/admin/AdminReports').then(m => ({ default: m.AdminReports })));
const AdminVerifications = lazyRetry(() => import('./pages/admin/AdminVerifications').then(m => ({ default: m.AdminVerifications })));
const AdminPlus          = lazyRetry(() => import('./pages/admin/AdminPlus').then(m => ({ default: m.AdminPlus })));
const AdminCredits       = lazyRetry(() => import('./pages/admin/AdminCredits').then(m => ({ default: m.AdminCredits })));
const AdminFeaturedAdmin = lazyRetry(() => import('./pages/admin/AdminFeaturedAdmin').then(m => ({ default: m.AdminFeaturedAdmin })));
const AdminFinance       = lazyRetry(() => import('./pages/admin/AdminFinance').then(m => ({ default: m.AdminFinance })));
const AdminNotifications = lazyRetry(() => import('./pages/admin/AdminNotifications').then(m => ({ default: m.AdminNotifications })));
const AdminInvites       = lazyRetry(() => import('./pages/admin/AdminInvites').then(m => ({ default: m.AdminInvites })));
const AdminSupport       = lazyRetry(() => import('./pages/admin/AdminSupport').then(m => ({ default: m.AdminSupport })));
const AdminWaitlist      = lazyRetry(() => import('./pages/admin/AdminWaitlist').then(m => ({ default: m.AdminWaitlist })));
const Waitlist           = lazyRetry(() => import('./pages/Waitlist').then(m => ({ default: m.Waitlist })));

import { WarningModal } from './components/WarningModal';
import { CookieBanner } from './components/CookieBanner';
import { MetaPixelTracker } from './components/MetaPixelTracker';
import { RequireVerifiedEmail } from './components/RequireVerifiedEmail';
import { RequireActiveArea } from './components/RequireActiveArea';
import { useToast } from './context/ToastContext';
import { setGlobalToast } from './services/api';

// Spoločný Suspense fallback — minimalistický (neflashne pri rýchlom chunk loade)
const PageFallback = () => <div style={{ minHeight: '100vh', background: 'transparent' }} />;

function App() {
  const { showToast } = useToast();

  useEffect(() => {
    setGlobalToast(showToast);
    return () => setGlobalToast(null);
  }, [showToast]);

  return (
    <>
    <WarningModal />
    <CookieBanner />
    <MetaPixelTracker />
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/account-banned" element={<AccountBanned />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/profile" element={<RequireVerifiedEmail><Profile /></RequireVerifiedEmail>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profile/edit" element={<RequireVerifiedEmail><EditProfile /></RequireVerifiedEmail>} />
        <Route path="/profile/stats" element={<RequireVerifiedEmail><ProfileStats /></RequireVerifiedEmail>} />
        <Route path="/questionnaire" element={<Questionnaire />} />
        <Route path="/change-password" element={<RequireVerifiedEmail><ChangePassword /></RequireVerifiedEmail>} />
        <Route path="/catalog" element={<RequireVerifiedEmail><RequireActiveArea><Catalog /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/discover" element={<RequireVerifiedEmail><RequireActiveArea><Discover /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/likes" element={<RequireVerifiedEmail><RequireActiveArea><Likes /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/chat" element={<RequireVerifiedEmail><RequireActiveArea><ChatList /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/chat/:id" element={<RequireVerifiedEmail><RequireActiveArea><ChatConversation /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/user/:id" element={<RequireVerifiedEmail><RequireActiveArea><UserProfile /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/verify-profile" element={<RequireVerifiedEmail><VerifyProfile /></RequireVerifiedEmail>} />
        <Route path="/featured" element={<RequireVerifiedEmail><RequireActiveArea><FeaturedBooking /></RequireActiveArea></RequireVerifiedEmail>} />
        <Route path="/plus" element={<RequireVerifiedEmail><Plus /></RequireVerifiedEmail>} />
        <Route path="/settings" element={<RequireVerifiedEmail><Settings /></RequireVerifiedEmail>} />
        <Route path="/settings/blocked" element={<RequireVerifiedEmail><BlockedUsers /></RequireVerifiedEmail>} />
        <Route path="/settings/payments" element={<RequireVerifiedEmail><PaymentHistory /></RequireVerifiedEmail>} />
        <Route path="/settings/faq" element={<RequireVerifiedEmail><Faq /></RequireVerifiedEmail>} />
        <Route path="/settings/support" element={<RequireVerifiedEmail><Support /></RequireVerifiedEmail>} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/waiting-area" element={<RequireVerifiedEmail><WaitingArea /></RequireVerifiedEmail>} />

        {/* Admin panel */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="verifications" element={<AdminVerifications />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="plus" element={<AdminPlus />} />
          <Route path="credits" element={<AdminCredits />} />
          <Route path="featured-admin" element={<AdminFeaturedAdmin />} />
          <Route path="finance" element={<AdminFinance />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="invites" element={<AdminInvites />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="waitlist" element={<AdminWaitlist />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </>
  );
}

export default App;
